import { useEffect, useState } from 'react';

/**
 * Hash routing, as the app does it. Nothing here carries a secret — every
 * route is an id the operator may already see — but staying hash-routed keeps
 * `sites.ts` simple: every real route is `/`, and the staged bundle needs no
 * rewrite rules to reach any of them.
 */

export type Route =
  | { readonly kind: 'overview' }
  | { readonly kind: 'accounts' }
  | { readonly kind: 'account'; readonly id: string }
  | { readonly kind: 'frames' }
  | { readonly kind: 'frame'; readonly id: string }
  | { readonly kind: 'photos' }
  | { readonly kind: 'screens' }
  | { readonly kind: 'albums' }
  | { readonly kind: 'audit' }
  | { readonly kind: 'subscriptions' }
  | { readonly kind: 'email' };

export interface Located {
  readonly route: Route;
  /** The query string after the hash path — filters, paging, search. */
  readonly query: URLSearchParams;
}

export const parseRoute = (hash: string): Located => {
  const [rawPath = '', rawQuery = ''] = hash.replace(/^#/, '').split('?');
  const segments = rawPath.split('/').filter(Boolean);
  const query = new URLSearchParams(rawQuery);
  const [head, second] = segments;

  const route: Route =
    head === 'accounts' && second
      ? { kind: 'account', id: second }
      : head === 'accounts'
        ? { kind: 'accounts' }
        : head === 'frames' && second
          ? { kind: 'frame', id: second }
          : head === 'frames'
            ? { kind: 'frames' }
            : head === 'photos'
              ? { kind: 'photos' }
              : head === 'screens'
                ? { kind: 'screens' }
                : head === 'albums'
                  ? { kind: 'albums' }
                  : head === 'audit'
                    ? { kind: 'audit' }
                    : head === 'subscriptions'
                      ? { kind: 'subscriptions' }
                      : head === 'email'
                        ? { kind: 'email' }
                        : { kind: 'overview' };

  return { route, query };
};

export const useRoute = (): Located => {
  const [located, setLocated] = useState<Located>(() => parseRoute(window.location.hash));

  useEffect(() => {
    const onChange = () => setLocated(parseRoute(window.location.hash));
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);

  return located;
};

export const href = (path: string, query?: Record<string, string | number | undefined>): string => {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== '' && value !== 0) params.set(key, String(value));
  }
  const qs = params.toString();
  return `#${path}${qs ? `?${qs}` : ''}`;
};

export const go = (path: string, query?: Record<string, string | number | undefined>): void => {
  window.location.hash = href(path, query).slice(1);
};
